/** @format */

import { useState, useEffect, useRef } from "@wordpress/element";
import MeetingViewLayout from "./MeetingViewLayout.jsx";

const MeetingView = ({ meetingPostId }) => {
	const [activeTab, setActiveTab] = useState(() => {
		const params = new URLSearchParams(window.location.search);
		const queryTab = params.get("tab") || "about";
		return queryTab === "events" ? "about" : queryTab;
	});
	const [meeting, setMeeting] = useState(null);
	const [isLoggedIn, setIsLoggedIn] = useState(true);
	const [loading, setLoading] = useState(true);

	// Running Filters State - Defaults directly to Day 1
	const [selectedDayFilter, setSelectedDayFilter] = useState(() => {
		const params = new URLSearchParams(window.location.search);
		return params.get("mday") || "Day 1";
	});
	const [selectedDisciplineFilter, setSelectedDisciplineFilter] = useState("All");
	const [trackingDisciplines, setTrackingDisciplines] = useState([]);

	const isFirstRender = useRef(true);

	const syncMeetingMetadata = () => {
		const globalData = window.ammData;

		if(!globalData?.api_url || !meetingPostId) {
			setLoading(false);
			return;
		}

		fetch(`${ globalData.api_url }/meetings/${ meetingPostId }`, {
			headers: { "X-WP-Nonce": globalData.nonce },
		})
			.then((res) => {
				if(res.status === 401) {
					setIsLoggedIn(false);
					throw new Error("Unauthorized context scope.");
				}
				return res.json();
			})
			.then((data) => {
				setMeeting(data);
				setLoading(false);
			})
			.catch((err) => {
				console.error("❌ Meeting verification failure:", err);
				setLoading(false);
			});
	};

	// Fetch Data on Mount / ID Shift
	useEffect(() => {
		syncMeetingMetadata();
	}, [meetingPostId]);

	// Extract disciplines dynamically based *only* on items belonging to the selected day
	useEffect(() => {
		if(!meeting?.schedule) return;

		const disciplinesForSelectedDay = [
			...new Set(
				meeting.schedule
					.filter((item) => `Day ${ item.dayNumber || 1 }` === selectedDayFilter)
					.map((item) => item.disciplineTypeName || item.disciplineName)
					.filter(Boolean)
			)
		].sort();

		setTrackingDisciplines(disciplinesForSelectedDay);

		// Safely snap discipline back to "All" if current selection doesn't exist on the newly chosen day
		if(selectedDisciplineFilter !== "All" && !disciplinesForSelectedDay.includes(selectedDisciplineFilter)) {
			setSelectedDisciplineFilter("All");
		}
	}, [selectedDayFilter, meeting, selectedDisciplineFilter]);

	// Sync Active Day Filter instantly with the Address Bar URL queries
	useEffect(() => {
		const url = new URL(window.location.href);
		const currentDayInUrl = url.searchParams.get("mday") || "";
		if(isFirstRender.current && !currentDayInUrl && selectedDayFilter === "Day 1") {
			isFirstRender.current = false;
			return;
		}
		if(selectedDayFilter !== currentDayInUrl) {
			url.searchParams.set("mday", selectedDayFilter);
			window.history.replaceState({}, "", url.toString());
		}
	}, [selectedDayFilter]);

	// Standard Tab Navigation Sync Handler
	const handleTabChange = (tabName) => {
		setActiveTab(tabName);
		const url = new URL(window.location.href);
		url.searchParams.set("tab", tabName);
		window.history.replaceState({}, "", url.toString());
	};

	// Derive basic structural properties to pass downstream
	const masterSchedule = meeting?.schedule || [];
	const uniqueDays = [
		...new Set(masterSchedule.map((item) => `Day ${ item.dayNumber || 1 }`))
	].sort();

	if(loading) {
		return (
			<div className="text-center py-5">
				<div className="spinner-border text-primary" role="status"></div>
			</div>
		);
	}

	return (
		<MeetingViewLayout
			meeting={ meeting }
			meetingPostId={ meetingPostId } // <-- Explicitly pass the verified ID down
			isLoggedIn={ isLoggedIn }
			setIsLoggedIn={ setIsLoggedIn }
			activeTab={ activeTab }
			handleTabChange={ handleTabChange }
			selectedDayFilter={ selectedDayFilter }
			setSelectedDayFilter={ setSelectedDayFilter }
			selectedDisciplineFilter={ selectedDisciplineFilter }
			setSelectedDisciplineFilter={ setSelectedDisciplineFilter }
			trackingDisciplines={ trackingDisciplines }
			uniqueDays={ uniqueDays }
			masterSchedule={ masterSchedule }
			syncMeetingMetadata={ syncMeetingMetadata }
		/>
	);
};

export default MeetingView;